// Copyright 2025-2026 NXGN Management, LLC. All Rights Reserved.
import { useState, useCallback, MouseEvent } from 'react'
import {
  Accordion,
  Button,
  FileUpload,
  Flex,
  Typography,
  Modal,
  Note,
  UseShowHide,
} from '@teamnine/pcj-ui-v2'
import { unzip } from 'unzipit'

import { Template as ITemplate } from '@/services/templates'
import { useErrorHelper } from '@/app/shared/hooks/useErrorHelper'
import { useUploadObjectData } from '@/app/Templates/hooks/useUploadObjectData'

type UploadObjectDataModalProps = {
  modal: UseShowHide
  template?: ITemplate
}

export default function UploadObjectDataModal({
  modal,
  template,
}: UploadObjectDataModalProps) {
  const { basicErrorHandler, displayGlobalMessage } = useErrorHelper()
  const [files, setFiles] = useState<File[]>([])
  const [zippedFiles, setZippedFiles] = useState<string[]>([])
  const [attachments, setAttachments] = useState<string[]>([])
  const { mutateAsync: uploadObjectData, isLoading } = useUploadObjectData()

  const onDrop: Parameters<typeof FileUpload>[0]['onDrop'] = useCallback(
    async (acceptedFiles: File[]) => {
      if (acceptedFiles.length === 0) return
      const file = acceptedFiles[0]
      setFiles([file])

      try {
        const { entries } = await unzip(file)
        const names = Object.keys(entries)
        setZippedFiles(
          names.filter(
            (name) => !name.endsWith('.zip') && !name.endsWith('.json')
          )
        )
        setAttachments(names.filter((name) => name.endsWith('.zip')))
      } catch (err) {
        console.error('Failed to read zip contents:', err)
        setZippedFiles([])
        setAttachments([])
      }
    },
    []
  )

  const onRemove = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
    setZippedFiles([])
    setAttachments([])
  }, [])

  const handleUpload = async () => {
    if (files.length === 0 || !template?.id) return
    try {
      await uploadObjectData({
        file: files[0],
        templateId: template.id,
      })
      displayGlobalMessage({
        heading: 'Object data successfully uploaded.',
        type: 'success',
      })
      setFiles([])
      modal.hide()
    } catch (err) {
      basicErrorHandler(err)
    }
  }

  return (
    <Modal open={modal.visible} onOpenChange={modal.toggle}>
      <Modal.Display aria-label="Upload Object Data">
        <Modal.Header title="Upload Object Data" />
        <Modal.Content>
          <Flex direction="column" gap={16}>
            <FileUpload
              multiple={false}
              maxFiles={1}
              isLocked={files.length > 0}
              onDrop={onDrop}
              accept={{ 'application/zip': ['.zip'] }}
            >
              {files.length === 0 ? (
                <Flex align="center" direction="column" gap={8}>
                  <Typography variant="16-medium">
                    Select a ZIP file containing CSV data, or click to browse
                  </Typography>
                  <Typography variant="14-regular" color="gray">
                    The file must contain valid object data.
                  </Typography>
                </Flex>
              ) : (
                files.map((file, index) => (
                  <Flex
                    key={index}
                    align="center"
                    justify="space-between"
                    gap={16}
                  >
                    <Typography variant="16-medium">{file.name}</Typography>
                    <Button
                      variant="blank"
                      onClick={(e: MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation()
                        onRemove(index)
                      }}
                    >
                      Remove
                    </Button>
                  </Flex>
                ))
              )}
            </FileUpload>
            {files.length > 0 &&
              (zippedFiles.length > 0 || attachments.length > 0) && (
                <Flex direction="column" gap={8}>
                  {zippedFiles.length > 0 && (
                    <Accordion title="Objects">
                      <Flex direction="column" gap={8}>
                        {zippedFiles.map((name) => (
                          <Typography key={name}>
                            {name.replace('.csv', '')}
                          </Typography>
                        ))}
                      </Flex>
                    </Accordion>
                  )}
                  {attachments.length > 0 && (
                    <Accordion title="Attachments">
                      <Flex direction="column" gap={8}>
                        {zippedFiles.map((name) => (
                          <Typography key={name}>
                            {name.replace('_attachments.zip', '')}
                          </Typography>
                        ))}
                      </Flex>
                    </Accordion>
                  )}
                </Flex>
              )}
            {template?.includesObjectData && (
              <Note>
                Uploading new object data will overwrite the existing object
                data for this template.
              </Note>
            )}
          </Flex>
        </Modal.Content>
        <Modal.Footer
          footerAction={{
            primaryButton: {
              label: 'Upload',
              onClick: handleUpload,
              type: 'button',
              loading: isLoading,
              disabled: files.length === 0,
            },
            secondaryButton: {
              label: 'Cancel',
              closeModal: true,
              onClick: () => setFiles([]),
            },
          }}
        />
      </Modal.Display>
    </Modal>
  )
}
